<template>
  <section v-if="data?.length">
    <div class="row justify-center items-center q-gutter-md q-pb-md heading">
      <div class="title">
        <h2 class="text-h4 text-weight-medium no-margin">
          {{ title }}
        </h2>
      </div>
    </div>

    <div class="row justify-center items-stretch q-col-gutter-md">
      <div
        v-for="item in data ?? []"
        :key="item.id"
        class="col-12 col-sm-6 col-lg-3 col-xl-2"
      >
        <q-item
          :to="{ path: `/brand/${item.slug}`, query: { category: category.slug } }"
          class="bg-blue-grey-9 rounded-borders"
        >
          <q-item-section style="height: 75px">
            <h3 class="text-h6 no-margin ellipsis-2-lines">
              {{ item.name }}
            </h3>
          </q-item-section>
          <q-item-section
            v-if="item.imageUrl"
            side
          >
            <q-img
              :src="item.imageUrl"
              width="75px"
              fit="contain"
              ratio="1"
              spinner-size="sm"
              spinner-color="primary"
            />
          </q-item-section>
        </q-item>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { Brand, Category, Product } from '~/types/search';

const props = defineProps({
  category: {
    type: Object as PropType<Category>,
    required: true,
  },
  size: {
    type: Number,
    default: 10,
  },
  title: {
    type: String,
    default: 'Shop By Brand',
  },
});

const { $typesense } = useNuxtApp();

const { buildFacetBy, buildFieldsList, buildFilterBy } = useTypesenseUtils();

const { data } = await useAsyncData(
  `categories_${props.category.id}_brands`,
  async () => {
    const buckets = await $typesense.search<Product>(
      'kygunco_products', {
        q: '*',
        query_by: '',
        filter_by: buildFilterBy<Product>({
          categories: {
            slug: {
              $eq: props.category.slug,
            },
          },
        }),
        facet_by: buildFacetBy<Product>(['manufacturer.slug']),
        max_facet_values: props.size,
        per_page: 0,
        enable_analytics: false,
      }).then(({ facet_counts }) => facet_counts?.[0]?.counts ?? []);

    const brands = await $typesense.search<Brand>(
      'kygunco_brands',
      {
        q: '*',
        query_by: '',
        filter_by: buildFilterBy<Brand>({
          slug: { $in: buckets.map(b => b.value) },
        }),
        include_fields: buildFieldsList<Brand>([
          'id',
          'name',
          'slug',
          'imageUrl',
        ]),
        highlight_fields: 'none',
        per_page: buckets.length,
      },
    ).then(({ hits }) => hits?.map(h => h.document) ?? []);

    const items: (Brand & { count: number })[] = [];

    for (const bucket of buckets) {
      const brand = brands.find(c => c.slug == bucket.value);

      if (!brand) {
        continue;
      }

      items.push({ ...brand, count: bucket.count });
    }

    return items;
  },
  {
    getCachedData: getCachedPayload,
  },
);
</script>
